import React, { useState } from 'react';
import ListView from 'components/Crud/views/List';
import { engineerDomain } from 'models';
import { QueryParams } from 'store/utils';
import { useEngineersState } from 'store/selectors';
import { TableCell } from '@material-ui/core';
import {
  useTableStyles,
  useTableRowActionsState,
  ListTable,
  TableActions
} from 'components/Crud/ListTable/ListTable';
import ListTableRow from 'components/Crud/ListTable/ListTableRow';
import FilterSearch, {
  FilterValue
} from 'components/FilterSearch/FilterSearch';
import useFilterSearchChips from 'utils/custom-hooks/useFilterSearchChips';
import { getFilterQueryParams } from 'utils/utils';
import { useDispatch } from 'react-redux';
import Engineer from 'models/Engineers';
import { fetchEngineers } from 'store/slices/engineers';
import { engineerFilters } from './formConfig';
import EngineersModal from './EngineersModal/EngineersModal';

const columnsWidth = [240, 160, 180, 160, 180];
const headers = ['Name', 'Company', 'Team Manager', 'Postcode', 'Phone'];

const EngineersView = () => {
  const tableClasses = useTableStyles();
  const { rowHovered, setRowHovered } = useTableRowActionsState();

  const { items: engineersData, page } = useEngineersState();
  const dispatch = useDispatch();

  const onSearch = (params: QueryParams) =>
    dispatch(fetchEngineers(params, { pageSize: page.pageSize, pageIndex: 0 }));

  const onLoad = (filterValues: FilterValue[], searchWithFilters: () => void) =>
    React.useEffect(() => {
      if (filterValues.length > 0) {
        searchWithFilters();
      }
    }, [filterValues]);

  const {
    params,
    filterValues,
    appliedFilters,
    onFilterRemove,
    onFilterValueInput
  } = useFilterSearchChips(engineerFilters, onSearch, onLoad);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEngineer, setCurrentEngineer] = useState<Engineer>();

  const openModal = () => setIsModalOpen(true);

  const cancelModal = () => {
    setIsModalOpen(false);
    setCurrentEngineer(undefined);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentEngineer(undefined);
  };

  return (
    <>
      <ListView
        domain={engineerDomain}
        dispatchProps={[params]}
        autoLoad={Boolean(filterValues.length === 0)}
      >
        {({ onEdit, onDelete, onView }) => (
          <section className={tableClasses.tableContainer}>
            <ListTable
              domain={engineerDomain}
              domainItems={engineersData}
              columnsWidth={columnsWidth}
              setRowHovered={(row: number | boolean) => setRowHovered(row)}
              rowHovered={rowHovered}
              title={engineerDomain.label}
              label={`A table of ${engineerDomain.plural}`}
              headers={headers}
              filter={getFilterQueryParams(appliedFilters)}
              headerComponent={
                <FilterSearch
                  filters={engineerFilters}
                  filterValues={appliedFilters}
                  onFilterRemove={onFilterRemove}
                  onFilterValueInput={onFilterValueInput}
                />
              }
              headerButtons={[
                {
                  label: `New ${engineerDomain.singular}`,
                  openModal
                }
              ]}
            >
              {engineersData.map((engineer, index) => (
                <ListTableRow
                  key={engineer.id}
                  data-testid={`tableRow-${engineer.id}`}
                  onClick={onView(engineer)}
                >
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-name`}
                  >
                    {`${engineer.firstName} ${engineer.lastName}`}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-company`}
                  >
                    {engineer.company}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-managerName`}
                  >
                    {engineer.team?.managerName}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-postcode`}
                  >
                    {engineer.postcode}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-phoneNumber`}
                  >
                    {engineer.phoneNumber}
                  </TableCell>
                  {rowHovered !== index ? (
                    <TableCell className={tableClasses.tableCell}>
                      &nbsp;
                    </TableCell>
                  ) : (
                    <TableActions
                      onEdit={onEdit}
                      onDelete={onDelete}
                      domain={engineerDomain}
                      domainItem={engineer}
                    />
                  )}
                </ListTableRow>
              ))}
            </ListTable>
          </section>
        )}
      </ListView>
      <EngineersModal
        open={isModalOpen}
        closeModal={closeModal}
        currentEngineer={currentEngineer}
        cancel={cancelModal}
      />
    </>
  );
};

export default EngineersView;
